import { Route, Routes, Navigate } from "react-router-dom";
import { ReactElement, useContext } from "react";
import Dashboard from "../pages/Dashboard";
import RegisterEmployeesPage from "../pages/RegisterEmployeesPage";
import Setting from "../pages/Settings";
import EmployeesPage from "../pages/EmployeesPage";
import UpdateEmployeesPage from "../pages/UpdateEmployeesPage";
import IncreamentHistoryPage from "../pages/IncreamentHistoryPage";
import FinancePage from "../pages/FinancePage";
import NewFinancePage from "../pages/NewFinancePage";
import UpdateFinancePage from "../pages/UpdateFinancePage";
import CategoryListsPage from "../pages/CategoryListsPage";
import NewCategoryPage from "../pages/NewCategoryPage";
import UpdateCategoryPage from "../pages/UpdateCategoryPage";
import InventoryBodyPage from "../pages/InventoryBodyPage";
import InventoryCategoriesPage from "../pages/InventoryCategoriesPage";
import NewInventoryCategoryPage from "../pages/NewInventoryCategoryPage";
import UpdateInventoryCategoryPage from "../pages/UpdateInventoryCategoryPage";
import InventoryItemsPage from "../pages/InventoryItemsPage";
import NewInventoryItemsPage from "../pages/NewInventoryItemsPage";
import UpdateInventoryItemPage from "../pages/UpdateInventoryItemPage";
import TeamPage from "../pages/TeamPage";
import NewTeamPage from "../pages/NewTeamPage";
import UpdateTeamsPage from "../pages/UpdateTeamsPage";
import TeamMembersPage from "../pages/TeamMembersPage";
import BankAccountsPage from "../pages/BankAccountsPage";
import AttendancePage from "../pages/AttendancePage";
import RolesPage from "../pages/RolesPage";
import RoleEmployeesPage from "../pages/RoleEmployeesPage";
import EmployeeEvaluationPage from "../pages/EmployeeEvaluationPage";
import EmployeeEvaluationDetailsPage from "../pages/EmployeeEvaluationDetailsPage";
import CvProcessorPageRoute from "../pages/CvProcessorPage";
import EnhancementsRoute from "../pages/EnhancementsPage";
import CvCategoriesRoute from "../pages/CvCategoriesPage";
import ArchivedCvsRoute from "../pages/ArchivedCvsPage";
import CoverLetterRoute from "../pages/CoverLetterPage";
import InterviewQaRoute from "../pages/InterviewQaPage";
import InterviewQaCategoriesRoute from "../pages/InterviewQaCategoriesPage";
import JobBoardRoute from "../pages/JobBoardPage";
import HiringContactsRoute from "../pages/HiringContactsPage";
import ResourcesRoute from "../pages/ResourcesPage";
import PromptLibraryRoute from "../pages/PromptLibraryPage";
import PromptLibraryCategoriesRoute from "../pages/PromptLibraryCategoriesPage";
import CrmRoute from "../pages/CrmPage";
import CrmAnalyticsRoute from "../pages/CrmAnalyticsPage";
import CrmDueTodayRoute from "../pages/CrmDueTodayPage";
import CrmLinkedInPipelineRoute from "../pages/CrmLinkedInPipelinePage";
import CrmLinkedInConversationsRoute from "../pages/CrmLinkedInConversationsPage";
import CrmLinkedInPerformanceRoute from "../pages/CrmLinkedInPerformancePage";
import PrimeContactsRoute from "../pages/PrimeContactsPage";
import EmailReviewQueueRoute from "../pages/EmailReviewQueuePage";
import CompaniesRoute from "../pages/CompaniesPage";
import CompanyDetailRoute from "../pages/CompanyDetailPage";
import PrimeContactProfileRoute from "../pages/PrimeContactProfilePage";
import SheetsRoute from "../pages/SheetsPage";
import SheetDetailRoute from "../pages/SheetDetailPage";
import DocumentsRoute from "../pages/DocumentsPage";
import DocumentDetailRoute from "../pages/DocumentDetailPage";
import MeetingsRoute from "../pages/MeetingsPage";
import { VerifyContext } from "../app/VerifyContext";

interface UserPageProps {
  name: string;
  superAdmin: boolean;
  canAccessEmployees: boolean;
  canAccessEmployeeEvaluation: boolean;
}

export default function UserPage({
  superAdmin,
}: UserPageProps) {
  const { effectivePermissions, canAccessCvProcessor, canAccessInterviewQa, canAccessJobBoard, canAccessHiringContacts, canAccessPromptLibrary, canAccessCrm, canAccessSheets, canAccessDocuments, canAccessMeetings } = useContext(VerifyContext);

  const withAdminGuard = (element: ReactElement) =>
    superAdmin ? element : <Navigate to="/" replace />;

  const withPermissionGuard = (page: string, element: ReactElement) => {
    if (superAdmin) return element;
    return effectivePermissions[page]?.can_view ? element : <Navigate to="/" replace />;
  };

  const withPermissionEditGuard = (page: string, element: ReactElement) => {
    if (superAdmin) return element;
    return effectivePermissions[page]?.can_edit ? element : <Navigate to="/" replace />;
  };

  return (
    <>
      <Routes>
        <Route path="/" element={<Dashboard />} />

        {/* Employees */}
        <Route path="/admin/employees" element={withPermissionGuard("employees", <EmployeesPage />)} />
        <Route path="/admin/employees/register-employees" element={withAdminGuard(<RegisterEmployeesPage />)} />
        <Route path="/admin/employees/update-employees/:employeeCode" element={withAdminGuard(<UpdateEmployeesPage />)} />
        <Route path="/admin/employees/increament-history/:employeeCode" element={withAdminGuard(<IncreamentHistoryPage />)} />

        {/* Finance */}
        <Route path="/admin/finance" element={withPermissionGuard("finance", <FinancePage />)} />
        <Route path="/admin/finance/new-finance" element={withPermissionEditGuard("finance", <NewFinancePage />)} />
        <Route path="/admin/finance/update-finance/:financeId" element={withPermissionEditGuard("finance", <UpdateFinancePage />)} />
        <Route path="/admin/finance/category-lists" element={withPermissionGuard("finance", <CategoryListsPage />)} />
        <Route path="/admin/finance/category-lists/new-category" element={withPermissionEditGuard("finance", <NewCategoryPage />)} />
        <Route path="/admin/finance/category-lists/update-category/:categoryId" element={withPermissionEditGuard("finance", <UpdateCategoryPage />)} />
        <Route path="/admin/finance/bank-accounts" element={withPermissionGuard("finance", <BankAccountsPage />)} />

        {/* Inventory */}
        <Route path="/admin/inventory" element={withPermissionGuard("inventory", <InventoryBodyPage />)} />
        <Route path="/admin/inventory/categories" element={withPermissionGuard("inventory", <InventoryCategoriesPage />)} />
        <Route path="/admin/inventory/new-category" element={withPermissionEditGuard("inventory", <NewInventoryCategoryPage />)} />
        <Route path="/admin/inventory/update-category/:categoryId" element={withPermissionEditGuard("inventory", <UpdateInventoryCategoryPage />)} />
        <Route path="/admin/inventory/items" element={withPermissionGuard("inventory", <InventoryItemsPage />)} />
        <Route path="/admin/inventory/new-items" element={withPermissionEditGuard("inventory", <NewInventoryItemsPage />)} />
        <Route path="/admin/inventory/update-items/:itemId" element={withPermissionEditGuard("inventory", <UpdateInventoryItemPage />)} />

        {/* Teams */}
        <Route path="/admin/teams" element={withPermissionGuard("teams", <TeamPage />)} />
        <Route path="/admin/teams/new-team" element={withPermissionEditGuard("teams", <NewTeamPage />)} />
        <Route path="/admin/teams/update-team/:teamId" element={withPermissionEditGuard("teams", <UpdateTeamsPage />)} />
        <Route path="/admin/teams/:teamId/members" element={withPermissionGuard("teams", <TeamMembersPage />)} />

        {/* Attendance */}
        <Route path="/admin/attendance" element={withPermissionGuard("attendance", <AttendancePage />)} />

        {/* Roles — always admin-only */}
        <Route path="/admin/roles" element={withAdminGuard(<RolesPage />)} />
        <Route path="/admin/roles/employees" element={withAdminGuard(<RoleEmployeesPage />)} />

        {/* Settings — accessible to all authenticated users */}
        <Route path="/admin/settings" element={<Setting />} />

        {/* Employee Evaluation */}
        <Route path="/admin/employee-evaluation" element={withPermissionGuard("evaluation", <EmployeeEvaluationPage />)} />
        <Route
          path="/admin/employee-evaluation/:employeeId"
          element={withPermissionGuard("evaluation", <EmployeeEvaluationDetailsPage />)}
        />

        {/* CV Processor — admin routes */}
        <Route
          path="/admin/cv-processor"
          element={superAdmin ? <CvProcessorPageRoute /> : (canAccessCvProcessor ? <Navigate to="/employee/cv-processor" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/cv-processor/enhancements"
          element={superAdmin ? <EnhancementsRoute /> : (canAccessCvProcessor ? <Navigate to="/employee/cv-processor/enhancements" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/cv-processor/categories"
          element={superAdmin ? <CvCategoriesRoute /> : (canAccessCvProcessor ? <Navigate to="/employee/cv-processor/categories" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/cv-processor/archived"
          element={superAdmin ? <ArchivedCvsRoute /> : (canAccessCvProcessor ? <Navigate to="/employee/cv-processor/archived" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/cv-processor/cover-letters"
          element={superAdmin ? <CoverLetterRoute /> : (canAccessCvProcessor ? <Navigate to="/employee/cv-processor/cover-letters" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/employee/cv-processor"
          element={canAccessCvProcessor ? <CvProcessorPageRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/cv-processor/enhancements"
          element={canAccessCvProcessor ? <EnhancementsRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/cv-processor/categories"
          element={canAccessCvProcessor ? <CvCategoriesRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/cv-processor/archived"
          element={canAccessCvProcessor ? <ArchivedCvsRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/cv-processor/cover-letters"
          element={canAccessCvProcessor ? <CoverLetterRoute /> : <Navigate to="/" replace />}
        />

        {/* Interview Q&A — admin routes */}
        <Route
          path="/admin/interview-qa"
          element={superAdmin ? <InterviewQaRoute /> : (canAccessInterviewQa ? <Navigate to="/employee/interview-qa" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/interview-qa/categories"
          element={superAdmin ? <InterviewQaCategoriesRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/interview-qa"
          element={canAccessInterviewQa ? <InterviewQaRoute /> : <Navigate to="/" replace />}
        />

        {/* Job Board */}
        <Route
          path="/admin/job-board"
          element={superAdmin ? <JobBoardRoute /> : (canAccessJobBoard ? <Navigate to="/employee/job-board" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/employee/job-board"
          element={canAccessJobBoard ? <JobBoardRoute /> : <Navigate to="/" replace />}
        />

        {/* LinkedIn Hiring Contacts */}
        <Route
          path="/admin/hiring-contacts"
          element={superAdmin ? <HiringContactsRoute /> : (canAccessHiringContacts ? <Navigate to="/employee/hiring-contacts" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/employee/hiring-contacts"
          element={canAccessHiringContacts ? <HiringContactsRoute /> : <Navigate to="/" replace />}
        />

        {/* Resources — open to everyone; individual documents are filtered by role */}
        <Route path="/resources" element={<ResourcesRoute />} />

        {/* Meetings */}
        <Route
          path="/admin/meetings"
          element={superAdmin ? <MeetingsRoute /> : (canAccessMeetings ? <Navigate to="/employee/meetings" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/employee/meetings"
          element={canAccessMeetings ? <MeetingsRoute /> : <Navigate to="/" replace />}
        />

        {/* Prompt Library */}
        <Route
          path="/admin/prompt-library"
          element={superAdmin ? <PromptLibraryRoute /> : (canAccessPromptLibrary ? <Navigate to="/employee/prompt-library" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/prompt-library/categories"
          element={withAdminGuard(<PromptLibraryCategoriesRoute />)}
        />
        <Route
          path="/employee/prompt-library"
          element={canAccessPromptLibrary ? <PromptLibraryRoute /> : <Navigate to="/" replace />}
        />

        {/* CRM */}
        <Route
          path="/admin/crm"
          element={superAdmin ? <CrmRoute /> : (canAccessCrm ? <Navigate to="/employee/crm" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/analytics"
          element={superAdmin ? <CrmAnalyticsRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/analytics" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/due-today"
          element={superAdmin ? <CrmDueTodayRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/due-today" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/due-today/pipeline"
          element={superAdmin ? <CrmLinkedInPipelineRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/due-today/pipeline" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/due-today/conversations"
          element={superAdmin ? <CrmLinkedInConversationsRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/due-today/conversations" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/due-today/performance"
          element={superAdmin ? <CrmLinkedInPerformanceRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/due-today/performance" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/prime-contacts"
          element={superAdmin ? <PrimeContactsRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/prime-contacts" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/prime-contacts/review-queue"
          element={superAdmin ? <EmailReviewQueueRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/prime-contacts/review-queue" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/companies"
          element={superAdmin ? <CompaniesRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/companies" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/companies/:id"
          element={superAdmin ? <CompanyDetailRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/companies" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/crm/prime-contacts/:id"
          element={superAdmin ? <PrimeContactProfileRoute /> : (canAccessCrm ? <Navigate to="/employee/crm/prime-contacts" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/employee/crm"
          element={canAccessCrm ? <CrmRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/analytics"
          element={canAccessCrm ? <CrmAnalyticsRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/due-today"
          element={canAccessCrm ? <CrmDueTodayRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/due-today/pipeline"
          element={canAccessCrm ? <CrmLinkedInPipelineRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/due-today/conversations"
          element={canAccessCrm ? <CrmLinkedInConversationsRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/due-today/performance"
          element={canAccessCrm ? <CrmLinkedInPerformanceRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/prime-contacts"
          element={canAccessCrm ? <PrimeContactsRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/prime-contacts/review-queue"
          element={canAccessCrm ? <EmailReviewQueueRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/companies"
          element={canAccessCrm ? <CompaniesRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/companies/:id"
          element={canAccessCrm ? <CompanyDetailRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/crm/prime-contacts/:id"
          element={canAccessCrm ? <PrimeContactProfileRoute /> : <Navigate to="/" replace />}
        />

        {/* Sheets */}
        <Route
          path="/admin/sheets"
          element={superAdmin ? <SheetsRoute /> : (canAccessSheets ? <Navigate to="/employee/sheets" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/sheets/:id"
          element={superAdmin ? <SheetDetailRoute /> : (canAccessSheets ? <Navigate to="/employee/sheets" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/employee/sheets"
          element={canAccessSheets ? <SheetsRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/sheets/:id"
          element={canAccessSheets ? <SheetDetailRoute /> : <Navigate to="/" replace />}
        />

        {/* Documents */}
        <Route
          path="/admin/documents"
          element={superAdmin ? <DocumentsRoute /> : (canAccessDocuments ? <Navigate to="/employee/documents" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/admin/documents/:id"
          element={superAdmin ? <DocumentDetailRoute /> : (canAccessDocuments ? <Navigate to="/employee/documents" replace /> : <Navigate to="/" replace />)}
        />
        <Route
          path="/employee/documents"
          element={canAccessDocuments ? <DocumentsRoute /> : <Navigate to="/" replace />}
        />
        <Route
          path="/employee/documents/:id"
          element={canAccessDocuments ? <DocumentDetailRoute /> : <Navigate to="/" replace />}
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
